/**
 * Composes a layer's frame-specific block styles with its authored HTML
 * style, and rewrites a text layer's content for unit stagger and content
 * actions. Shared by the Remotion overlay and the editor canvas, so a
 * scrubbed frame and a rendered one agree.
 */
import type { CSSProperties } from "react";
import {
  anchorTransform,
  layerCss,
  layerTransform,
} from "@hightouch/lib/ad-studio/html-editor/layer-css";
import { wrapTextUnits } from "@hightouch/lib/ad-studio/html-editor/wrap-text-units";
import type {
  Layer,
  SvgLayer,
  TextLayer,
} from "@hightouch/lib/ad-studio/html-editor/model";
import type {
  AnimationBlock,
  TextUnit,
} from "@hightouch/lib/ad-studio/html-editor/animation-blocks";
import {
  type BlockEvalContext,
  type BlockStyle,
  blockStylesForLayer,
} from "./block-style-at";
import { blockContentForLayer } from "./block-content-at";
import { unitStylesForLayer } from "./text-units";

function originCoordinates(origin: string): [string, string] | null {
  const parts = origin.trim().toLowerCase().split(/\s+/);
  if (
    parts.some(
      (part) =>
        !/^(left|right|top|bottom|center|0|[-+]?(?:\d*\.)?\d+(?:px|%|em|rem|vw|vh))$/.test(
          part,
        ),
    )
  )
    return null;
  const x = parts[0] ?? "50%";
  const y = parts[1] ?? "50%";
  const values: Record<string, string> = {
    "0": "0px",
    left: "0%",
    top: "0%",
    center: "50%",
    right: "100%",
    bottom: "100%",
  };
  if (x === "top" || x === "bottom" || y === "left" || y === "right") {
    return [values[y] ?? y, values[x] ?? x];
  }
  return [values[x] ?? x, values[y] ?? y];
}

function animationAboutOrigin(
  transform: string,
  desired: string,
  authored: string,
): string {
  const target = originCoordinates(desired);
  const source = originCoordinates(authored);
  if (!target || !source) return transform;
  const [dx, dy] = target;
  const [ax, ay] = source;
  if (dx === ax && dy === ay) return transform;
  // Conjugate only the animation; the authored transform keeps its own pivot.
  return `translate(calc(${dx} - ${ax}), calc(${dy} - ${ay})) ${transform} translate(calc(${ax} - ${dx}), calc(${ay} - ${dy}))`;
}

/** The px value of an authored border-radius, or undefined when it is not a
 * single px length (percentages and per-corner lists rest at 0). */
function authoredRadius(css: string | undefined): number | undefined {
  const match = css === undefined ? null : /^([\d.]+)px$/.exec(css.trim());
  return match ? Number(match[1]) : undefined;
}

/** The authored values the color and corner-radius timelines rest at. */
function restValues(
  layer: Layer,
  base: ReturnType<typeof layerCss>,
): BlockEvalContext["rest"] {
  return {
    color: base.get(layer.type === "text" ? "color" : "background-color"),
    borderRadius: authoredRadius(base.get("border-radius")),
  };
}

/**
 * CSS overrides for `layer` at `frame` from its whole-layer blocks (unit
 * blocks style the unit spans instead — see {@link blockAnimatedContent}),
 * or undefined when they contribute nothing at this frame.
 */
export function layerBlockStyle(
  layer: Layer,
  inFlow: boolean,
  frame: number,
  blocks: readonly AnimationBlock[],
  context: BlockEvalContext,
): CSSProperties | undefined {
  const wholeLayer = blocks.filter((block) => !block.text);
  const base = layerCss(layer, inFlow);
  const animated = blockStylesForLayer(wholeLayer, frame, {
    ...context,
    rest: restValues(layer, base),
  });
  if (!animated) return undefined;

  const style: CSSProperties = {};

  if (animated.transform) {
    // CSS executes right-to-left. The layer's own rotation and flips go
    // innermost, the animation wraps them, and a text layer's grow anchor
    // wraps everything: the anchor shifts the box to where the viewer sees
    // it, so it must be applied last or the animation pivots about the box
    // before the shift (a centered headline would scale from its right edge).
    style.transform = [
      anchorTransform(layer, inFlow),
      animationAboutOrigin(
        animated.transform,
        animated.transformOrigin ?? "50% 50%",
        base.get("transform-origin") ?? "50% 50%",
      ),
      layerTransform(layer),
    ]
      .filter((part): part is string => part !== undefined)
      .join(" ");
  }
  if (animated.opacity !== undefined) {
    const authored = Number(base.get("opacity") ?? "1");
    style.opacity = Number.isFinite(authored)
      ? authored * animated.opacity
      : animated.opacity;
  }
  if (animated.filter) {
    const authored = base.get("filter");
    style.filter = authored
      ? `${authored} ${animated.filter}`
      : animated.filter;
  }
  if (animated.clipPath) style.clipPath = animated.clipPath;
  if (animated.visibility) style.visibility = animated.visibility;
  if (animated.width !== undefined) style.width = animated.width;
  if (animated.height !== undefined) style.height = animated.height;
  // A color block paints the text on a text layer and the fill elsewhere.
  if (animated.color !== undefined) {
    if (layer.type === "text") style.color = animated.color;
    else style.backgroundColor = animated.color;
  }
  if (animated.borderRadius !== undefined) {
    style.borderRadius = `${animated.borderRadius}px`;
  }
  // Only an SVG layer has strokes to draw. Dash values are in path units, so
  // this relies on the pathLength="1" that blockAnimatedSvg stamps on every
  // shape: one inherited dash of 1 is then the whole shape, whatever its
  // real length, and the offset hides the undrawn remainder.
  if (animated.strokeDrawn !== undefined && layer.type === "svg") {
    style.strokeDasharray = 1;
    style.strokeDashoffset = 1 - animated.strokeDrawn;
  }
  return Object.keys(style).length ? style : undefined;
}

/** Shape elements a stroke block draws; the SVG2 pathLength attribute applies to each. */
const STROKED_SHAPE_TAG =
  /<(path|circle|ellipse|line|rect|polyline|polygon)\b(?![^>]*\bpathLength=)/gi;

/**
 * An SVG layer's markup ready for a stroke block: every shape carries
 * pathLength="1" so the inherited dash from {@link layerBlockStyle} measures
 * whole shapes. Stamped only when a stroke block exists, because pathLength
 * also rescales any dash pattern the markup authored itself. Returns the
 * input layer when nothing applies.
 */
export function blockAnimatedSvg(
  layer: SvgLayer,
  blocks: readonly AnimationBlock[],
): SvgLayer {
  if (!blocks.some((block) => block.action === "stroke")) return layer;
  const innerMarkup = layer.innerMarkup.replace(
    STROKED_SHAPE_TAG,
    '<$1 pathLength="1"',
  );
  return innerMarkup === layer.innerMarkup ? layer : { ...layer, innerMarkup };
}

// --- Unit-span styling ---

/**
 * Unit spans are `<span data-ht-unit="i">…</span>`, wrapped at render time by
 * {@link blockAnimatedContent} and carrying no style attribute of their own
 * (this module owns their style).
 */
function unitSpan(attribute = "data-ht-unit"): RegExp {
  return new RegExp(`<span(\\s[^>]*?)?\\s${attribute}="(\\d+)"([^>]*)>`, "g");
}

/**
 * Frames a block's text stagger adds after its own duration: the last unit
 * starts `(units - 1) * delay` late (smoothing keeps that window). Zero for a
 * block without a text spec or on a layer that is not text.
 */
export function blockStaggerFrames(
  block: AnimationBlock,
  layer: Layer,
): number {
  if (!block.text || layer.type !== "text") return 0;
  const count = countUnits(wrapTextUnits(layer.content, block.text.unit));
  return Math.max(0, count - 1) * block.text.delay;
}

/** How many stagger units the content carries (highest index + 1). */
export function countUnits(
  content: string,
  attribute = "data-ht-unit",
): number {
  let max = -1;
  for (const match of content.matchAll(unitSpan(attribute))) {
    max = Math.max(max, Number(match[2]));
  }
  return max + 1;
}

function styleAttribute(style: BlockStyle): string {
  const parts: string[] = [];
  if (style.opacity !== undefined) parts.push(`opacity:${style.opacity}`);
  if (style.transform) {
    // Transforms are inert on inline elements; unit spans must opt out.
    parts.push(`display:inline-block`, `transform:${style.transform}`);
  }
  if (style.transformOrigin) {
    parts.push(`transform-origin:${style.transformOrigin}`);
  }
  if (style.filter) parts.push(`filter:${style.filter}`);
  if (style.clipPath) parts.push(`clip-path:${style.clipPath}`);
  if (style.visibility) parts.push(`visibility:${style.visibility}`);
  if (style.color) parts.push(`color:${style.color}`);
  return parts.join(";");
}

/**
 * Inject per-unit inline styles into the content's `data-ht-unit` spans.
 * Units whose style is null (identity) are left untouched.
 */
export function styledUnitContent(
  content: string,
  styles: readonly (BlockStyle | null)[],
  attribute = "data-ht-unit",
): string {
  return content.replace(unitSpan(attribute), (match, _pre, index: string) => {
    const style = styles[Number(index)];
    if (!style) return match;
    const css = styleAttribute(style);
    if (!css) return match;
    return `${match.slice(0, -1)} style="${css}">`;
  });
}

const escapeHtml = (text: string): string =>
  text.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");

/** The layer content's plain text — the starting point change-text
 * transitions away from. Tag-strip + basic entity decode is enough here;
 * text-layer content is simple inline markup. */
const plainText = (content: string): string =>
  content
    .replace(/<[^>]*>/g, "")
    .replaceAll("&amp;", "&")
    .replaceAll("&lt;", "<")
    .replaceAll("&gt;", ">")
    .replaceAll("&nbsp;", " ");

/**
 * A text layer's content at `frame`: content actions (counter/change-text)
 * replace the text, then unit-staggered blocks style the unit spans. The
 * authored markup carries no unit spans (the Doc stores plain text so a
 * change of unit needs no re-save), so they are wrapped here, nesting each
 * distinct unit type. Returns the input layer when nothing applies.
 */
export function blockAnimatedContent(
  layer: TextLayer,
  frame: number,
  blocks: readonly AnimationBlock[],
  context: BlockEvalContext,
): TextLayer {
  let content = layer.content;

  const override = blockContentForLayer(blocks, frame, plainText(content));
  if (override !== null) content = escapeHtml(override);

  const unitBlocks = blocks.filter((block) => block.text);
  const units = (["line", "word", "letter"] as const).filter((unit) =>
    unitBlocks.some((block) => block.text?.unit === unit),
  );
  const attributeFor = (unit: TextUnit) =>
    units.length === 1 ? "data-ht-unit" : (`data-ht-unit-${unit}` as const);
  for (const unit of units) {
    content = wrapTextUnits(content, unit, attributeFor(unit));
  }
  for (const unit of units) {
    const attribute = attributeFor(unit);
    const count = countUnits(content, attribute);
    content = styledUnitContent(
      content,
      unitStylesForLayer(
        unitBlocks.filter((block) => block.text?.unit === unit),
        count,
        frame,
        { ...context, rest: restValues(layer, layerCss(layer, false)) },
        layer.id,
      ),
      attribute,
    );
  }

  return content === layer.content ? layer : { ...layer, content };
}
